import React, { SyntheticEvent, useEffect, useState } from 'react'
import { flowRight as compose } from 'lodash'
import { RouteChildrenProps, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  CompanyList,
  CompanySearchAndFilter,
  CompanyService,
  formatList,
  HEADERS,
  HEADERS_LOADING,
  BREADCRUMBS_LIST,
  COMPANYTEMPLATE,
} from 'features/Company'
import { Button, Pagination, Table, BulletLabel } from 'components'
import { THead } from 'components/Table'
import { IconImport, IconPlus } from 'components/Icons'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import { LIMIT_PAGE } from 'helpers/constants/common'
import { numberStart, onPageChange } from 'helpers/common'
import { useBreadcrumbs, useCancelToken, useSearchParams } from 'helpers/hooks'

import type { CompanyForList, ISorting } from 'types'
import { OnboardingContainer } from 'containers/Onboarding'
import { downloadTemplate } from 'helpers/download'

function CompanyContainer({ location }: RouteChildrenProps): JSX.Element {
  const [loading, setLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [companies, setCompanies] = useState<CompanyForList[]>([])
  const history = useHistory()
  const [searchParams, setSearchParams] = useSearchParams({
    _limit: 30,
    _page: 1,
    isActive: true,
  })

  const { t } = useTranslation(['company', 'layout'])
  const { newCancelToken, isCancel } = useCancelToken()
  const [isOpenOnboarding, setIsOpenOnboarding] = useState(false)
  const [template, setTemplateType] = useState<string>('')

  const _page: number = Number(searchParams._page) || 1
  const base = 'con-company'

  useBreadcrumbs(BREADCRUMBS_LIST)

  const handleSortChange = (sort: ISorting): void =>
    setSearchParams({ _sort: sort.column, _order: sort.order, _page: 1 })

  const getCompanies = async () => {
    try {
      setIsError(false)
      setLoading(true)

      const companies = await CompanyService.findAll(searchParams, {
        cancelToken: newCancelToken(),
      })
      const { body, totals } = companies
      const _companies = formatList(body, numberStart(LIMIT_PAGE, _page))

      setTotalPages(Math.ceil(Number(totals) / LIMIT_PAGE) || 1)
      setCompanies(_companies)
      setLoading(false)
    } catch (e) {
      if (isCancel(e)) return

      setLoading(false)
      setIsError(true)
    }
  }

  const getExcelTemplate = async () => {
    try {
      const template = await CompanyService.downloadCompanyTemplate()
      downloadTemplate(template, COMPANYTEMPLATE)
    } catch (e) {
      if (isCancel(e)) return
    }
  }

  const handleClickRow = (id: string) => (e: SyntheticEvent) => {
    e.preventDefault()
    history.push(`${routes.company.path}/${id}/detail`)
  }

  useEffect(() => {
    getCompanies()
  }, [location])

  return (
    <div className='container-list'>
      <CompanySearchAndFilter isLoading={loading} />

      <div className='float-button--wrap'>
        <div className='float-button--group'>
          <div className='float-button--box--button'>
            <Button
              variants='outline'
              withIcon={<IconImport variants='sm' />}
              onClick={() => {
                setTemplateType('company')
                setIsOpenOnboarding(true)
              }}
            >
              {t('company:add.button')}
            </Button>
            <Button
              onClick={() => history.push(routes.company.create.path)}
              variants='primary'
              withIcon={<IconPlus variants='sm' />}
            >
              {t('company:create.button')}
            </Button>
          </div>
        </div>
      </div>

      <div className='list-content'>
        <div className='content-bullet'>
          <BulletLabel isActive isShowLabel />
          <BulletLabel isActive={false} isShowLabel />
        </div>
        <div className='row'>
          <Table>
            <THead
              columns={HEADERS}
              onSortChange={handleSortChange}
              sorting={{
                column: searchParams._sort as string,
                order: searchParams._order || 'ASC',
              }}
              position='left'
            />
            <CompanyList
              columns={loading ? HEADERS_LOADING : HEADERS}
              companies={companies}
              loading={loading}
              isError={isError}
              onClickRow={handleClickRow}
            />
          </Table>
        </div>
      </div>

      <div className={`${base}__con-bottom`}>
        <Pagination
          currentPage={_page}
          totalPages={totalPages}
          onChange={(page) =>
            onPageChange(() => setSearchParams({ _page: page }))
          }
          disabled={loading}
        />
      </div>

      <OnboardingContainer
        isOpen={isOpenOnboarding}
        onClose={() => setIsOpenOnboarding(false)}
        type={template}
        onDownload={() => getExcelTemplate()}
      />
    </div>
  )
}

export default compose(withBackOfficeCms)(CompanyContainer)
